import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card } from '../components/ui/card';
import { Heart, ArrowLeft, Mail, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';

export const AuthPage = () => {
  const [searchParams] = useSearchParams();
  // Default to signup form if coming from pricing to checkout
  const [isLogin, setIsLogin] = useState(searchParams.get('redirect') !== 'checkout');
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotPasswordSent, setForgotPasswordSent] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);
  const { login, register, user, token, API } = useAuth();
  const navigate = useNavigate();

  const decodeUserIdFromToken = (rawToken) => {
    try {
      if (!rawToken) return null;
      const payload = rawToken.split('.')[1];
      if (!payload) return null;
      const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
      const paddedBase64 = `${base64}${'='.repeat((4 - (base64.length % 4)) % 4)}`;
      const decoded = JSON.parse(atob(paddedBase64));
      return decoded?.sub || null;
    } catch {
      return null;
    }
  };

  const triggerPendingIAP = (fallbackToken = null) => {
    const pendingRaw = sessionStorage.getItem('pendingIAP');
    if (!pendingRaw) return false;

    const iapHandler = window?.webkit?.messageHandlers?.iapHandler;
    if (!iapHandler?.postMessage) return false;

    try {
      const pending = JSON.parse(pendingRaw);
      const userId = user?.id || decodeUserIdFromToken(fallbackToken || token);
      if (!userId) return false;

      iapHandler.postMessage({
        action: pending.action,
        productId: pending.productId,
        packageId: pending.productId,
        userId
      });
      sessionStorage.removeItem('pendingIAP');
      return true;
    } catch {
      return false;
    }
  };

  // Check if user is already logged in and needs to go to checkout
  useEffect(() => {
    const redirect = searchParams.get('redirect');
    if (user && redirect === 'checkout') {
      const selectedPlan = sessionStorage.getItem('selectedPlan');
      if (selectedPlan) {
        handleCheckoutRedirect(selectedPlan);
      }
    }
    if (user && redirect === 'iap') {
      const posted = triggerPendingIAP();
      navigate(posted ? '/pricing' : '/dashboard');
    }
  }, [user]);

  const handleCheckoutRedirect = async (planId) => {
    try {
      const res = await fetch(`${API}/subscriptions/checkout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          plan_id: planId,
          origin_url: window.location.origin
        })
      });

      if (!res.ok) throw new Error('Failed to create checkout session');

      const data = await res.json();
      sessionStorage.removeItem('selectedPlan');
      
      if (data.promo_applied && data.promo_message) {
        toast.success(data.promo_message);
      }
      
      window.location.href = data.checkout_url;
    } catch (err) {
      console.error('Checkout redirect error:', err);
      sessionStorage.removeItem('selectedPlan');
      navigate('/pricing');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const redirectToCheckout = searchParams.get('redirect') === 'checkout';
      const redirectToIAP = searchParams.get('redirect') === 'iap';
      const selectedPlan = sessionStorage.getItem('selectedPlan');

      if (isLogin) {
        const authResult = await login(email, password);
        toast.success('Welcome back!');
        
        if (redirectToIAP && authResult?.token) {
          const posted = triggerPendingIAP(authResult.token);
          navigate(posted ? '/pricing' : '/dashboard');
          return;
        }

        if (redirectToCheckout && selectedPlan && authResult?.token) {
          await handleCheckoutWithToken(selectedPlan, authResult.token);
          return;
        }
        navigate('/dashboard');
      } else {
        const authResult = await register(email, password, name);
        toast.success('Account created successfully!');
        
        if (redirectToIAP && authResult?.token) {
          const posted = triggerPendingIAP(authResult.token);
          navigate(posted ? '/pricing' : '/dashboard');
          return;
        }

        if (redirectToCheckout && selectedPlan && authResult?.token) {
          await handleCheckoutWithToken(selectedPlan, authResult.token);
          return;
        }
        navigate('/dashboard');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Authentication failed');
      setLoading(false);
    }
  };

  const handleCheckoutWithToken = async (planId, authToken) => {
    try {
      const res = await fetch(`${API}/subscriptions/checkout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify({
          plan_id: planId,
          origin_url: window.location.origin
        })
      });

      if (!res.ok) throw new Error('Failed to create checkout session');

      const data = await res.json();
      sessionStorage.removeItem('selectedPlan');
      
      if (data.promo_applied && data.promo_message) {
        toast.success(data.promo_message);
      }
      
      window.location.href = data.checkout_url;
    } catch (err) {
      console.error('Checkout error:', err);
      toast.error('Failed to start checkout. Please try again from pricing page.');
      sessionStorage.removeItem('selectedPlan');
      setLoading(false);
      navigate('/pricing');
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!email) {
      toast.error('Please enter your email address');
      return;
    }
    
    setLoading(true);
    try {
      const res = await fetch(`${API}/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });
      
      if (res.ok) {
        setForgotPasswordSent(true);
        toast.success('Check your email for reset instructions');
      } else {
        const data = await res.json();
        toast.error(data.detail || 'Something went wrong');
      }
    } catch (error) {
      toast.error('Failed to send reset email');
    } finally {
      setLoading(false);
    }
  };

  // Forgot Password View
  if (showForgotPassword) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#FAFAF9] to-[#F4E4BA]/20 flex items-center justify-center p-6">
        <Card className="w-full max-w-md bg-white rounded-xl border border-stone-100 shadow-[0_8px_24px_rgba(124,154,146,0.15)] p-8">
          {forgotPasswordSent ? (
            <div className="text-center space-y-4">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto">
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
              <h2 className="text-2xl font-nunito font-bold text-[#2D2D2D]">Check Your Email</h2>
              <p className="text-[#57534E]">
                We've sent a password reset link to <strong>{email}</strong>
              </p>
              <p className="text-sm text-[#A8A29E]">
                The link will expire in 1 hour. If you don't see the email, check your spam folder.
              </p>
              <Button
                type="button"
                onClick={() => { setShowForgotPassword(false); setForgotPasswordSent(false); }}
                className="w-full bg-primary text-white hover:bg-primary/90 rounded-full h-12 font-semibold mt-4"
              >
                Back to Sign In
              </Button>
            </div>
          ) : (
            <form onSubmit={handleForgotPassword} className="space-y-6">
              <button
                type="button"
                onClick={() => setShowForgotPassword(false)}
                className="flex items-center text-[#57534E] hover:text-primary"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Sign In
              </button>
              
              <div className="text-center space-y-2">
                <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Mail className="w-8 h-8 text-primary" />
                </div>
                <h2 className="text-2xl font-nunito font-bold text-[#2D2D2D]">Forgot Password?</h2>
                <p className="text-[#57534E]">
                  No worries! Enter your email and we'll send you reset instructions.
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="reset-email" className="text-[#2D2D2D] font-medium">Email</Label>
                <Input
                  id="reset-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="bg-stone-50 border-stone-200 focus:border-primary focus:ring-2 focus:ring-primary/20 rounded-lg h-12"
                  placeholder="Enter your email"
                  data-testid="forgot-password-email"
                />
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-primary text-white hover:bg-primary/90 rounded-full h-12 font-semibold"
                data-testid="forgot-password-submit"
              >
                {loading ? 'Sending...' : 'Send Reset Link'}
              </Button>
            </form>
          )}
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#FAFAF9] to-[#F4E4BA]/20 flex items-center justify-center p-6">
      <div className="w-full max-w-6xl">
        <Button 
          onClick={() => navigate('/')} 
          variant="ghost" 
          className="mb-6 text-primary hover:text-primary/80"
          data-testid="auth-back-button"
        >
          <ArrowLeft className="w-5 h-5 mr-2" />
          Back
        </Button>
        
        <div className="grid md:grid-cols-2 gap-12 items-center">
        <div className="space-y-6">
          <div className="flex items-center gap-3">
            <Heart className="w-12 h-12 text-primary fill-primary" strokeWidth={1.5} />
            <h1 className="text-4xl md:text-5xl font-nunito font-bold text-primary">
              Calm Pathways
            </h1>
          </div>
          <p className="text-xl md:text-2xl text-[#57534E] leading-relaxed">
            Comprehensive support and education for families navigating PANDAS/PANS
          </p>
          <div className="space-y-3 text-[#57534E]">
            <div className="flex items-start gap-3">
              <div className="w-2 h-2 rounded-full bg-primary mt-2" />
              <p>In-depth medical education and resources</p>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-2 h-2 rounded-full bg-primary mt-2" />
              <p>Track symptoms and identify triggers</p>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-2 h-2 rounded-full bg-primary mt-2" />
              <p>Connect with supportive community</p>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-2 h-2 rounded-full bg-primary mt-2" />
              <p>AI-powered guidance available 24/7</p>
            </div>
          </div>
        </div>

        <Card className="bg-white rounded-xl border border-stone-100 shadow-[0_8px_24px_rgba(124,154,146,0.15)] p-8">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="text-center space-y-2">
              <h2 className="text-2xl font-nunito font-bold text-[#2D2D2D]">
                {isLogin ? 'Welcome Back' : 'Join Our Community'}
              </h2>
              <p className="text-[#57534E]">
                {isLogin ? 'Sign in to access your account' : 'Create your account to get started'}
              </p>
            </div>

            {!isLogin && (
              <div className="space-y-2">
                <Label htmlFor="name" className="text-[#2D2D2D] font-medium">Full Name</Label>
                <Input
                  id="name"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required={!isLogin}
                  className="bg-stone-50 border-stone-200 focus:border-primary focus:ring-2 focus:ring-primary/20 rounded-lg h-12"
                  placeholder="Enter your name"
                  data-testid="auth-name-input"
                />
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="email" className="text-[#2D2D2D] font-medium">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="bg-stone-50 border-stone-200 focus:border-primary focus:ring-2 focus:ring-primary/20 rounded-lg h-12"
                placeholder="Enter your email"
                data-testid="auth-email-input"
              />
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="password" className="text-[#2D2D2D] font-medium">Password</Label>
                {isLogin && (
                  <button
                    type="button"
                    onClick={() => setShowForgotPassword(true)}
                    className="text-sm text-primary hover:text-primary/80"
                    data-testid="forgot-password-link"
                  >
                    Forgot password?
                  </button>
                )}
              </div>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="bg-stone-50 border-stone-200 focus:border-primary focus:ring-2 focus:ring-primary/20 rounded-lg h-12"
                placeholder="Enter your password"
                data-testid="auth-password-input"
              />
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-primary text-white hover:bg-primary/90 rounded-full h-12 font-semibold shadow-sm transition-transform hover:scale-[1.02]"
              data-testid="auth-submit-button"
            >
              {loading ? 'Please wait...' : isLogin ? 'Sign In' : 'Create Account'}
            </Button>

            <div className="text-center">
              {isLogin ? (
                <button
                  type="button"
                  onClick={() => setIsLogin(false)}
                  className="text-primary hover:text-primary/80 font-medium"
                  data-testid="auth-toggle-button"
                >
                  Don't have an account? Create one
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => setIsLogin(true)}
                  className="text-primary hover:text-primary/80 font-medium"
                  data-testid="auth-toggle-button"
                >
                  Already have an account? Sign in
                </button>
              )}
            </div>
          </form>
        </Card>
        </div>
      </div>
    </div>
  );
};

export default AuthPage;
